import { useState, useCallback, useMemo } from 'preact/hooks';
import { Pencil, Trash, ArrowCounterClockwise, Plus, Check, X } from '@phosphor-icons/react';
import { api, type SpotPosition, type SpotStageEntry } from '../../services/api.js';
import { useToast } from '../shared/Toast.js';
import {
  formatSignedUsd,
  formatSignedRub,
  formatUsdPrice,
  formatQty,
  formatPct,
} from './spotUtils.js';

const TP_LABEL  = { tp1: 'TP1', tp2: 'TP2', tp3: 'TP3' } as const;

interface PositionCardProps {
  position: SpotPosition;
  onEdit: (position: SpotPosition) => void;
  onDelete: (position: SpotPosition) => void;
  onResetTp: (position: SpotPosition) => void;
  onChanged: () => void;
}

export function PositionCard({ position, onEdit, onDelete, onResetTp, onChanged }: PositionCardProps) {
  const isProfit = position.pnlUsd >= 0;
  const isClosed = position.closedAt != null;

  const buys = useMemo(
    () => (position.stages ?? []).slice().sort((a, b) => a.executedAt.localeCompare(b.executedAt)),
    [position.stages],
  );

  return (
    <article class={`spot-card ${isProfit ? 'profit' : 'loss'} ${isClosed ? 'closed' : ''}`}>
      <div class="spot-card-summary">
      <div class="spot-pnl">
        <div class={`spot-pnl-main ${isProfit ? 'profit' : 'loss'}`}>
          {formatSignedUsd(position.pnlUsd)}
        </div>
        <div class={`spot-pnl-side ${isProfit ? 'profit' : 'loss'}`}>
          <span class="pct">{formatPct(position.pnlPct)}</span>
          <span class="rub">{formatSignedRub(position.pnlRub)}</span>
        </div>
      </div>

      <div class="spot-stats">
        <div class="spot-stat">
          <span class="spot-stat-label">Средняя</span>
          <span class="spot-stat-value">{formatUsdPrice(position.avgPriceUsd)}</span>
        </div>
        <div class="spot-stat">
          <span class="spot-stat-label">Текущая</span>
          <span class="spot-stat-value accent">{formatUsdPrice(position.currentPriceUsd)}</span>
        </div>
        <div class="spot-stat">
          <span class="spot-stat-label">Количество</span>
          <span class="spot-stat-value">{formatQty(position.qty)} {position.symbol.toUpperCase()}</span>
        </div>
        <div class="spot-stat">
          <span class="spot-stat-label">Стоимость</span>
          <span class="spot-stat-value">${position.valueUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        <div class="spot-stat">
          <span class="spot-stat-label">Потрачено</span>
          <span class="spot-stat-value">${position.totalSpentUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        <div class="spot-stat">
          <span class="spot-stat-label">В рублях</span>
          <span class="spot-stat-value">{position.valueRub.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽</span>
        </div>
      </div>

      </div>{/* /spot-card-summary */}

      <div class="spot-card-detail">
      <div class="spot-card-targets">
      {position.isFutures && <FuturesCloseSection position={position} />}

      {!position.isFutures && (
      <div class="spot-tp-section">
        <div class="spot-section-title">Цели фиксации прибыли</div>
        <div class="spot-stages-groups">
          {(['tp1', 'tp2', 'tp3'] as const).map((key) => {
            const tp = position.tp[key];
            if (!tp.price) {
              return (
                <div class="spot-stage-group empty" key={key}>
                  <div class="spot-stage-group-head">
                    <span class="spot-stage-tag">
                      {TP_LABEL[key]}
                      <span class="spot-stage-sublabel">цель не задана</span>
                    </span>
                    <div class="spot-stage-group-progress">
                      <div class="spot-stage-progress-bar">
                        <div class="spot-stage-progress-fill" style={{ transform: 'scaleX(0)' }} />
                      </div>
                      <div class="spot-stage-group-meta">
                        <span class="filled-amount">—</span>
                        <span class="fill-pct">0%</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            const fillPct = tp.reached ? 100 : Math.max(0, Math.min(100, tp.progressPct));
            const distLabel = tp.distancePct !== null
              ? `${tp.distancePct > 0 ? '+' : ''}${tp.distancePct.toFixed(2)}% до цели`
              : '';
            const profit = tp.profitAtTpUsd !== null
              ? `${tp.profitAtTpUsd >= 0 ? '+' : '-'}$${Math.abs(tp.profitAtTpUsd).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
              : '';
            return (
              <div class={`spot-stage-group ${tp.reached ? 'filled' : 'partial'}`} key={key}>
                <div class="spot-stage-group-head">
                  <span class="spot-stage-tag">
                    {tp.reached ? `✓ ${TP_LABEL[key]}` : TP_LABEL[key]}
                    <span class="spot-stage-sublabel">{distLabel}</span>
                  </span>
                  <div class="spot-stage-group-progress">
                    <div class="spot-stage-progress-bar" aria-label={`Progress to ${TP_LABEL[key]}`}>
                      <div
                        class={`spot-stage-progress-fill ${tp.reached ? 'filled' : ''}`}
                        style={{ transform: `scaleX(${fillPct / 100})` }}
                      />
                    </div>
                    <div class="spot-stage-group-meta">
                      <span class="filled-amount">
                        {formatUsdPrice(tp.price)}
                        {profit && <span class="of"> · {profit}</span>}
                      </span>
                      <span class="fill-pct">{fillPct.toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}
      </div>{/* /spot-card-targets */}

      {/* Докупки — скрыты для закрытой позиции (её остаётся только удалить) */}
      {!isClosed && <BuysSection position={position} buys={buys} onChanged={onChanged} />}
      </div>{/* /spot-card-detail */}

      <div class="spot-card-foot">
        {!isClosed && (
          <button class="spot-foot-btn" onClick={() => onEdit(position)}>
            <Pencil size={14} /><span>Изменить</span>
          </button>
        )}
        {!position.isFutures && !isClosed && (
          <button class="spot-foot-btn" onClick={() => onResetTp(position)}>
            <ArrowCounterClockwise size={14} /><span>Сбросить TP</span>
          </button>
        )}
        <button class="spot-foot-btn danger" onClick={() => onDelete(position)}>
          <Trash size={14} /><span>Удалить</span>
        </button>
      </div>
    </article>
  );
}

// ── Futures close-price target + delivery status ────────────────

interface DeliveryStatus {
  kind: 'ok' | 'pending' | 'error';
  label: string;
}

/** Derive the Telegram delivery state for a closed futures position. */
export function futuresDeliveryStatus(position: SpotPosition): DeliveryStatus | null {
  if (position.closedAt == null) return null;
  if (position.notifiedAt != null) return { kind: 'ok', label: 'Telegram: доставлено ✓' };
  if (position.notifyAttempts > 0) {
    return { kind: 'error', label: `Telegram: не дошло, повтор (попыток: ${position.notifyAttempts})` };
  }
  return { kind: 'pending', label: 'Telegram: отправка…' };
}

function FuturesCloseSection({ position }: { position: SpotPosition }) {
  const isClosed = position.closedAt != null;
  const dirWord = position.closeDirection === 'down' ? 'при падении' : 'при росте';
  const delivery = futuresDeliveryStatus(position);

  // Profit if/when the position closes at a given price (entry avg × qty basis).
  const profitAt = (price: number | null): { pnl: number; pct: number } | null => {
    if (price == null) return null;
    const pnl = (price - position.avgPriceUsd) * position.qty;
    const pct = position.totalSpentUsd > 0 ? (pnl / position.totalSpentUsd) * 100 : 0;
    return { pnl, pct };
  };

  const renderProfit = (label: string, price: number | null) => {
    const p = profitAt(price);
    if (p === null) return null;
    return (
      <div class="spot-close-projection">
        <span class="spot-close-proj-label">{label}</span>
        <span class={`spot-close-proj-value ${p.pnl >= 0 ? 'profit' : 'loss'}`}>
          {formatSignedUsd(p.pnl)} · {p.pct >= 0 ? '+' : ''}{p.pct.toFixed(2)}%
        </span>
      </div>
    );
  };

  if (isClosed) {
    const closedPrice = position.closedAtPrice ?? position.closePrice;
    return (
      <div class="spot-close-section closed">
        <div class="spot-close-banner">
          <span class="spot-close-flag">● ПОЗИЦИЯ ЗАКРЫТА ПО ЦЕЛИ</span>
          {closedPrice != null && (
            <span class="spot-close-price">{dirWord} · {formatUsdPrice(closedPrice)}</span>
          )}
        </div>
        {renderProfit('Прибыль по закрытию', closedPrice)}
        {delivery && (
          <div class={`spot-deliv ${delivery.kind}`}>{delivery.label}</div>
        )}
        <div class="spot-close-hint">Проверь, что ордер исполнился на бирже, затем удали позицию.</div>
      </div>
    );
  }

  if (position.closePrice != null) {
    return (
      <div class="spot-close-section armed">
        <div class="spot-section-title">Цель закрытия</div>
        <div class="spot-close-armed-row">
          <span class="spot-close-target">{formatUsdPrice(position.closePrice)}</span>
          <span class="spot-close-armed-meta">сработает {dirWord} до цели · ТГ-алерт</span>
        </div>
        {renderProfit('Прибыль при закрытии', position.closePrice)}
      </div>
    );
  }

  return (
    <div class="spot-close-section empty">
      <div class="spot-section-title">Цель закрытия</div>
      <div class="spot-close-armed-meta">не задана — укажи в «Изменить», чтобы получить алерт при закрытии.</div>
    </div>
  );
}

// ── Buys section (один плоский список докупок) ──────────────────

interface BuysSectionProps {
  position: SpotPosition;
  buys: SpotStageEntry[];
  onChanged: () => void;
}

function BuysSection({ position, buys, onChanged }: BuysSectionProps) {
  const [adding, setAdding] = useState(false);
  const canRemove = buys.length > 1;

  return (
    <div class="spot-stages">
      <div class="spot-section-title">Докупки</div>

      {buys.length > 0 && (
        <div class="spot-stage-entries">
          {buys.map((e) => (
            <BuyRow
              key={e.entryId}
              position={position}
              entry={e}
              canRemove={canRemove}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}

      {adding ? (
        <BuyAddForm
          position={position}
          currentPrice={position.currentPriceUsd}
          onCancel={() => setAdding(false)}
          onSaved={() => { setAdding(false); onChanged(); }}
        />
      ) : (
        <button class="spot-stage-add-btn" onClick={() => setAdding(true)} title="Докупить">
          <Plus size={11} />
          <span>Докупить</span>
        </button>
      )}
    </div>
  );
}

interface BuyRowProps {
  position: SpotPosition;
  entry: SpotStageEntry;
  canRemove: boolean;
  onChanged: () => void;
}

function BuyRow({ position, entry, canRemove, onChanged }: BuyRowProps) {
  const { show: toast } = useToast();
  const amount = entry.qty * entry.pricePerCoinUsd;

  const handleRemove = useCallback(async () => {
    if (!confirm(`Откатить эту покупку?\n${formatQty(entry.qty)} ${position.symbol.toUpperCase()} @ ${formatUsdPrice(entry.pricePerCoinUsd)}`)) return;
    try {
      await api.removeStageEntry(position.id, entry.entryId);
      toast('Покупка откатана', 'success');
      onChanged();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ошибка';
      toast(`Не удалось откатить: ${msg}`, 'error');
    }
  }, [position.id, position.symbol, entry.entryId, entry.qty, entry.pricePerCoinUsd, toast, onChanged]);

  return (
    <div class="spot-stage-entry">
      <span class="spot-stage-entry-main">
        {formatQty(entry.qty)} {position.symbol.toUpperCase()} @ {formatUsdPrice(entry.pricePerCoinUsd)}
      </span>
      <span class="spot-stage-entry-amount">
        ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
      {canRemove ? (
        <button class="spot-stage-undo" onClick={handleRemove} title="Откатить покупку">
          <X size={10} />
        </button>
      ) : (
        <span class="spot-stage-undo-placeholder" />
      )}
    </div>
  );
}

interface BuyAddFormProps {
  position: SpotPosition;
  currentPrice: number;
  onCancel: () => void;
  onSaved: () => void;
}

function BuyAddForm({ position, currentPrice, onCancel, onSaved }: BuyAddFormProps) {
  const { show: toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [qty, setQty] = useState('');
  const [amount, setAmount] = useState('');

  const qNum = parseFloat(qty);
  const aNum = parseFloat(amount);
  const computedPrice = (Number.isFinite(qNum) && qNum > 0 && Number.isFinite(aNum) && aNum > 0)
    ? aNum / qNum
    : null;

  const handleSave = useCallback(async () => {
    const q = parseFloat(qty);
    const a = parseFloat(amount);

    if (!Number.isFinite(q) || q <= 0) { toast('Укажите количество', 'error'); return; }
    if (!Number.isFinite(a) || a <= 0) { toast('Укажите сумму', 'error'); return; }

    const p = a / q;
    setSubmitting(true);
    try {
      await api.recordStage(position.id, { qty: q, pricePerCoinUsd: p });
      toast(`Докупка $${a.toFixed(2)}`, 'success');
      onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Ошибка';
      toast(`Ошибка: ${msg}`, 'error');
    } finally {
      setSubmitting(false);
    }
  }, [qty, amount, position.id, toast, onSaved]);

  return (
    <div class="spot-stage-add-form">
      <div class="spot-stage-form">
        <label class="spot-stage-field">
          <span class="spot-stage-field-label">Кол-во {position.symbol.toUpperCase()}</span>
          <input class="spot-stage-input" inputMode="decimal" placeholder="0.00000000" value={qty}
            onInput={(e) => setQty((e.target as HTMLInputElement).value)} disabled={submitting} />
        </label>
        <label class="spot-stage-field">
          <span class="spot-stage-field-label">Сумма $</span>
          <input class="spot-stage-input" inputMode="decimal" placeholder="0.00" value={amount}
            onInput={(e) => setAmount((e.target as HTMLInputElement).value)} disabled={submitting} />
        </label>
      </div>
      <div class="spot-stage-computed-price">
        {computedPrice !== null
          ? `Цена: ${formatUsdPrice(computedPrice)}`
          : (currentPrice > 0 ? `Текущая: ${formatUsdPrice(currentPrice)}` : '')}
      </div>
      <div class="spot-stage-actions">
        <button class="spot-stage-btn save" onClick={handleSave} disabled={submitting} title="Сохранить">
          <Check size={12} />
        </button>
        <button class="spot-stage-btn cancel" onClick={onCancel} disabled={submitting} title="Отмена">
          <X size={12} />
        </button>
      </div>
    </div>
  );
}
